<template>
  <div class="composite-action">
    <div class="composite-action__row">
      <div
        class="composite-action__field composite-action__field--grow multiselect-wrap--small"
        :class="{ 'has-error': hasError('TYPE_REQUIRED') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.TYPE') }}
        </label>
        <multiselect
          :value="selectedType"
          track-by="id"
          label="name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="160"
          :options="activityTypes"
          :allow-empty="false"
          @input="update({ activity_type: $event.id })"
        />
      </div>
      <label
        class="composite-action__field composite-action__field--amount"
        :class="{ 'has-error': hasError('SCHEDULED_IN_REQUIRED') }"
      >
        <span class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.SCHEDULED_IN') }}
        </span>
        <input
          :value="params.scheduled_in"
          type="text"
          inputmode="numeric"
          class="answer--text-input"
          :placeholder="
            $t(
              'AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.SCHEDULED_IN_PLACEHOLDER'
            )
          "
          @input="onAmountInput"
        />
      </label>
      <div
        class="composite-action__field composite-action__field--unit multiselect-wrap--small"
        :class="{ 'has-error': hasError('SCHEDULED_UNIT_REQUIRED') }"
      >
        <label class="composite-action__label">
          {{ $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.SCHEDULED_UNIT') }}
        </label>
        <multiselect
          :value="selectedUnit"
          track-by="id"
          label="name"
          :placeholder="$t('FORMS.MULTISELECT.SELECT')"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :max-height="160"
          :options="unitOptions"
          :allow-empty="false"
          @input="update({ scheduled_unit: $event.id })"
        />
      </div>
    </div>
    <label class="composite-action__field">
      <span class="composite-action__label">
        {{ $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.DESCRIPTION') }}
      </span>
      <textarea
        :value="params.description"
        rows="2"
        maxlength="1000"
        :placeholder="
          $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.DESCRIPTION_PLACEHOLDER')
        "
        @input="update({ description: $event.target.value })"
      />
    </label>
    <p class="composite-action__hint">
      {{ $t('AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.HINT') }}
    </p>
    <ul v-if="showErrors && errors.length" class="composite-action__errors">
      <li v-for="error in errors" :key="error">
        {{ $t(`AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.ERRORS.${error}`) }}
      </li>
    </ul>
  </div>
</template>

<script>
import { sanitizeIntegerInput } from 'dashboard/helper/numericInputHelper';
import {
  OPPORTUNITY_ACTIVITY_SCHEDULE_UNITS,
  compositeActionParams,
  opportunityActivityActionErrors,
} from 'dashboard/helper/automationOpportunityActionHelper';

export default {
  name: 'AutomationActionOpportunityActivityInput',
  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
    activityTypes: {
      type: Array,
      default: () => [],
    },
    showErrors: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    params() {
      return compositeActionParams(this.value);
    },
    unitOptions() {
      return OPPORTUNITY_ACTIVITY_SCHEDULE_UNITS.map(unit => ({
        id: unit,
        name: this.$t(
          `AUTOMATION.ACTION.OPPORTUNITY_ACTIVITY.UNITS.${unit.toUpperCase()}`
        ),
      }));
    },
    selectedType() {
      return (
        this.activityTypes.find(
          type => type.id === this.params.activity_type
        ) || null
      );
    },
    selectedUnit() {
      return (
        this.unitOptions.find(unit => unit.id === this.params.scheduled_unit) ||
        null
      );
    },
    errors() {
      return opportunityActivityActionErrors(this.params);
    },
  },
  methods: {
    update(changes) {
      this.$emit('input', { ...this.params, ...changes });
    },
    hasError(error) {
      return this.showErrors && this.errors.includes(error);
    },
    onAmountInput(event) {
      const sanitized = sanitizeIntegerInput(event.target.value);
      event.target.value = sanitized;
      this.update({ scheduled_in: sanitized === '' ? '' : Number(sanitized) });
    },
  },
};
</script>

<style lang="scss" scoped>
.composite-action {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.composite-action__row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-small);
}

.composite-action__field {
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 0;

  input,
  textarea {
    margin-bottom: 0;
  }

  &.has-error .composite-action__label {
    color: var(--r-500);
  }
}

.composite-action__field--grow {
  flex: 1 1 20rem;
}

.composite-action__field--amount {
  flex: 0 0 17rem;
}

.composite-action__field--unit {
  flex: 0 0 14rem;
}

.composite-action__label {
  color: var(--s-700);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--space-micro);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.composite-action__hint {
  color: var(--s-600);
  font-size: var(--font-size-mini);
  margin: 0;
}

.composite-action__errors {
  color: var(--r-500);
  font-size: var(--font-size-small);
  list-style: none;
  margin: 0;
}
</style>
